import { Chatbot, DisplayMode, SendCallback } from '@justrelate/chatbot';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { Obj, connect, currentLanguage, load } from 'scrivito';

import { BotConfigResolver } from './botConfigResolver';

interface AiAssistantProps {
  botId: string;
  conversationKey: number;
  defaultDisplayMode: DisplayMode;
  onHide: () => void;
  isVisible: boolean;
  userPrompt?: string;
  onSend?: SendCallback;
}

export const AiAssistant = connect(function AiAssistant({
  botId,
  conversationKey,
  defaultDisplayMode,
  onHide,
  isVisible,
  userPrompt,
  onSend,
}: AiAssistantProps) {
  const rawConfig = Obj.onAllSites().get(botId);

  const [config, setConfig] =
    useState<ComponentProps<typeof Chatbot>['config']>(null);

  const previousUserPrompt = useRef(userPrompt);
  if (previousUserPrompt.current !== userPrompt) {
    previousUserPrompt.current = userPrompt;
    // prevent stale userPrompt when a new conversation is started
    setConfig(null);
  }

  useEffect(() => {
    if (!rawConfig) return;

    const resolvedConfig = new BotConfigResolver(rawConfig);
    let cancelled = false;

    (async () => {
      const loadedConfig = await load(() => {
        return {
          systemPrompt: resolvedConfig.systemPrompt(),
          tools: [resolvedConfig.resourcesReadTool()],
          temperature: resolvedConfig.temperature(),
          useAnonymously: true,
          initialMessages: buildInitialMessages(
            userPrompt,
            resolvedConfig.greeting(),
          ),
        };
      });

      if (!cancelled) setConfig(loadedConfig);
    })();

    return () => {
      cancelled = true;
    };
  }, [rawConfig, userPrompt]);

  return (
    <Chatbot
      config={config}
      language={siteLanguage()}
      defaultDisplayMode={defaultDisplayMode}
      onHide={onHide}
      isVisible={isVisible}
      key={conversationKey}
      onSend={onSend}
    />
  );
});

function siteLanguage(): 'de' | 'en' | 'fr' {
  const lang = currentLanguage();
  return lang === 'de' || lang === 'fr' ? lang : 'en';
}

function buildInitialMessages(
  userPrompt: string | undefined,
  greeting: string | undefined,
) {
  if (userPrompt) return [{ role: 'user' as const, content: userPrompt }];
  if (greeting) return [{ role: 'assistant' as const, content: greeting }];
  return [];
}
